import { useAudioPlayer } from '@/hooks/useAudioPlayer'
// src/components/custom-components/AudioPlayer/AudioPlayer.tsx
import { Subtract } from '@mynaui/icons-react'
import { Headphones } from 'lucide-react'
import { Loader } from '../Loader'
import AudioControls from './AudioControls'

export type AudioPlayerProps = {
  isPopout?: boolean
}

const AudioPlayer = ({ isPopout = false }: AudioPlayerProps) => {
  // Destructure everything the hook returns
  const {
    containerRef,
    isPopoutOpen,
    isExpanded,
    isLoading,
    isFetching,
    currentTitle,
    currentArtist,
    currentArtwork,
    currentDuration,
    currentProgress,
    isPlaying,
    handlePlayPause,
    toNextTrack,
    toPrevTrack,
    onScrub,
    onScrubEnd,
    handleInteraction,
    openPopout,
  } = useAudioPlayer(isPopout)

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleInteraction}
      onMouseLeave={() => {
        /* Collapse after timeout is managed inside the hook */
      }}
      onClick={handleInteraction}
      className="fixed bottom-4 right-4 z-50"
    >
      {/* If popout is open elsewhere and this is not the popout instance, render nothing */}
      {isPopoutOpen && !isPopout ? null : !isExpanded && !isPopout ? (
        <div className="bg-customConicGradient w-12 h-12 rounded-full flex items-center justify-center shadow-lg cursor-pointer">
          <Headphones className="w-6 h-6 text-white" />
        </div>
      ) : (
        <div className="flex justify-start items-center px-4 py-3 gap-2 bg-white rounded-xl shadow-lg w-[320px] transition-all">
          {/* Artwork / Loader */}
          <div className="bg-customConicGradient w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 mx-auto my-auto">
            {isLoading || isFetching ? (
              <div className="flex w-full h-full items-center justify-center">
                <Loader />
              </div>
            ) : (
              <img
                src={currentArtwork}
                alt={currentTitle || 'Artwork'}
                className="w-full h-full object-cover rounded-lg"
              />
            )}
          </div>

          {/* Track info & controls */}
          <div className="flex flex-col gap-3 items-start w-full">
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-col gap-0.5">
                <h4 className="text-sm font-semibold">
                  {isLoading || isFetching ? 'Loading...' : currentTitle || 'Unknown'}
                </h4>
                <p className="text-xs text-gray-500">{isLoading || isFetching ? '' : currentArtist || ''}</p>
              </div>
              <div className={isLoading || isFetching ? 'opacity-50 pointer-events-none' : ''}>
                <AudioControls
                  isPlaying={isPlaying}
                  onPrevClick={toPrevTrack}
                  onNextClick={toNextTrack}
                  onPlayPauseClick={(play) => handlePlayPause(play)}
                />
              </div>
            </div>

            {/* Progress bar + Popout button */}
            <div className="w-full flex items-center gap-2">
              <input
                type="range"
                value={currentProgress}
                step="1"
                min="0"
                max={currentDuration || 0}
                onChange={(e) => onScrub(Number(e.target.value))}
                onMouseUp={onScrubEnd}
                onKeyUp={onScrubEnd}
                className="w-full h-2"
                disabled={isLoading || isFetching}
              />
              {!isPopout && (
                <Subtract
                  className="w-6 h-6 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    openPopout()
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AudioPlayer
